import { memo, useCallback } from "react";
import type { KeyboardEvent, MouseEvent } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AlertCircleIcon,
  ChartLineData01Icon,
  Loading03Icon,
  RefreshIcon,
} from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";
import { BUSINESS_STATUS_MAP } from "@/features/company/types/company.types";
import type { DispatchCompanyListItem } from "@/features/company";
import { useCompanySummary, useSummaryFetcher } from "../hooks/useOrderAnalytics";
import { useOrderAnalyticsStore } from "../stores/orderAnalyticsStore";
import {
  COUNT_METRICS,
  RATE_METRICS,
  calcRate,
  formatCount,
  formatRate,
} from "../constants/metrics";
import { validatePeriod } from "../utils/period";
import type { CompanyStatsSummary } from "../types/orderAnalytics.types";

const ignoreRejection = () => {};

const SummaryBody = memo(function SummaryBody({
  summary,
}: {
  summary: CompanyStatsSummary;
}) {
  return (
    <>
      <dl className="grid grid-cols-3 gap-x-2 gap-y-3 px-3 py-3">
        {COUNT_METRICS.map((metric) => (
          <div key={metric.key} className="min-w-0">
            <dt className="flex items-center gap-1 text-[11px] text-gray-500">
              <span
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded",
                  metric.iconClass,
                )}
              >
                <HugeiconsIcon icon={metric.icon} size={12} />
              </span>
              <span className="truncate">{metric.label}</span>
            </dt>
            <dd className="mt-1 flex items-baseline gap-0.5">
              <span className="text-lg leading-none font-bold text-gray-900">
                {formatCount(summary[metric.key], metric.digits)}
              </span>
              {metric.unit && (
                <span className="text-[11px] text-gray-400">{metric.unit}</span>
              )}
            </dd>
          </div>
        ))}
      </dl>

      <dl className="space-y-2 border-t border-gray-100 px-3 py-3">
        {RATE_METRICS.map((metric) => {
          const numerator = summary[metric.numerator.key];
          const denominator = summary[metric.denominator.key];
          return (
            <div key={metric.key} className="flex items-start gap-2">
              <span
                className={cn(
                  "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded",
                  metric.iconClass,
                )}
              >
                <HugeiconsIcon icon={metric.icon} size={12} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <dt className="truncate text-[11px] text-gray-500">
                    {metric.label}
                  </dt>
                  <dd className="flex items-baseline gap-0.5">
                    <span className="text-lg leading-none font-bold text-gray-900">
                      {formatRate(calcRate(numerator, denominator))}
                    </span>
                    <span className="text-[11px] text-gray-400">%</span>
                  </dd>
                </div>
                <p className="mt-0.5 truncate text-[11px] text-gray-400">
                  {metric.numerator.label}: {formatCount(numerator)}건 /{" "}
                  {metric.denominator.label}: {formatCount(denominator)}건
                </p>
              </div>
            </div>
          );
        })}
      </dl>
    </>
  );
});

interface CompanyAnalyticsCardProps {
  company: DispatchCompanyListItem;
}

export const CompanyAnalyticsCard = memo(function CompanyAnalyticsCard({
  company,
}: CompanyAnalyticsCardProps) {
  const { companyGuid, companyName } = company;
  const period = useOrderAnalyticsStore((s) => s.period);
  const openTrend = useOrderAnalyticsStore((s) => s.openTrend);
  const { data, error, isFetching } = useCompanySummary(companyGuid);
  const { fetchSummary, refreshSummary } = useSummaryFetcher();

  const isPeriodValid = validatePeriod(period) === null;
  const status = BUSINESS_STATUS_MAP[company.businessStatus];

  const handleFetch = useCallback(() => {
    if (!isPeriodValid) return;
    fetchSummary(companyGuid, period).catch(ignoreRejection);
  }, [companyGuid, fetchSummary, isPeriodValid, period]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleFetch();
      }
    },
    [handleFetch],
  );

  const handleRefresh = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (!isPeriodValid) return;
      refreshSummary(companyGuid, period).catch(ignoreRejection);
    },
    [companyGuid, isPeriodValid, period, refreshSummary],
  );

  const handleOpenTrend = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      openTrend({ companyGuid, companyName });
    },
    [companyGuid, companyName, openTrend],
  );

  const isAggregationBehind =
    !!data && data.aggregatedThrough !== null && data.aggregatedThrough < data.endDate;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${companyName} 요약 지표 조회`}
      onClick={handleFetch}
      onKeyDown={handleKeyDown}
      className="flex cursor-pointer flex-col overflow-hidden rounded-lg border border-gray-200 bg-white transition-colors outline-none hover:border-gray-300 focus-visible:border-blue-500"
    >
      <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-3 py-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h2 className="truncate text-sm font-bold text-gray-900">
              {companyName}
            </h2>
            {status && (
              <span
                className={cn(
                  "shrink-0 rounded-full px-1.5 py-0.5 text-xs",
                  status.color,
                )}
              >
                {status.label}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-[11px] text-gray-400 tabular-nums">
            {data ? (
              <>
                {data.startDate} ~ {data.endDate}
                {data.aggregatedThrough === null && " · 집계 데이터 없음"}
                {isAggregationBehind && ` · 집계 ~${data.aggregatedThrough}`}
              </>
            ) : isFetching ? (
              "조회 중"
            ) : error ? (
              "조회 실패"
            ) : (
              "미조회"
            )}
          </p>
        </div>
        {data && (
          <button
            type="button"
            aria-label="새로고침"
            title="새로고침"
            disabled={isFetching}
            onClick={handleRefresh}
            className="flex size-6 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
          >
            <HugeiconsIcon
              icon={RefreshIcon}
              size={14}
              className={cn(isFetching && "animate-spin")}
            />
          </button>
        )}
      </div>

      <div className="relative min-h-[236px] flex-1">
        {data ? (
          <SummaryBody summary={data} />
        ) : isFetching ? (
          <div className="flex h-full min-h-[236px] flex-col items-center justify-center gap-2 text-xs text-gray-400">
            <HugeiconsIcon icon={Loading03Icon} size={20} className="animate-spin" />
            조회 중...
          </div>
        ) : error ? (
          <div className="flex h-full min-h-[236px] flex-col items-center justify-center gap-2 px-4 text-center">
            <HugeiconsIcon icon={AlertCircleIcon} size={20} className="text-red-500" />
            <p className="text-xs text-red-600">조회에 실패했습니다</p>
            <p className="line-clamp-2 text-[11px] text-gray-400">{error.message}</p>
            <button
              type="button"
              disabled={!isPeriodValid}
              onClick={(e) => {
                e.stopPropagation();
                handleFetch();
              }}
              className="mt-1 h-7 rounded-md border border-gray-300 bg-white px-2.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              재시도
            </button>
          </div>
        ) : (
          <div className="flex h-full min-h-[236px] items-center justify-center text-xs text-gray-400">
            {isPeriodValid ? "카드를 클릭하면 조회합니다" : "기간을 확인해 주세요"}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-3 py-2">
        <p className="min-w-0 truncate text-[11px] text-amber-600">
          {data && data.orderCntMissingDays > 0
            ? `오더(취소 제외) ${data.orderCntMissingDays}일치 미반영 (백필 중)`
            : ""}
        </p>
        <button
          type="button"
          onClick={handleOpenTrend}
          className="flex h-7 shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 text-xs text-gray-700 hover:bg-gray-50"
        >
          <HugeiconsIcon icon={ChartLineData01Icon} size={14} />
          자세히보기
        </button>
      </div>
    </div>
  );
});
